import { useTranslation } from 'react-i18next';

import { RangeSection } from './RangeSection';
import { ResultTabs } from './ResultTabs';
import { SongSection } from './SongSection';
import { SummarySection } from './SummarySection';
import { SustainSection } from './SustainSection';
import { isMeasuredNoisy } from '../../roomNoise';
import { visibleTabs } from '../../visibleTabs';

import type { DiagnosisRecord } from '../../record';
import type { MetricTabId } from '../../resultCopy';

/**
 * 결과 화면 본문(덱 p10) — 진단 1·2·3 + 종합.
 *
 * 덱 p10 의 마지막 줄("'고음 전환 훈련'이 준비되면 알림으로 알려 드릴게요")은 그리지 않는다 —
 * 아직 없는 기능과 없는 알림을 약속하는 문장이고, 심사(App Store 2.1)는 그것을 미완 표식으로
 * 읽는다. 훈련과 푸시가 생기는 날 그 기능의 입구로 다시 세운다.
 *
 * **값의 정본은 회차 레코드 하나다.** 리포트(`Report`)를 같이 받지 않는 이유: 레코드에
 * 없는 값(엔진의 가중 overall, 스펙트럼 축)을 화면이 집으면 홈·이력과 다른 숫자가 나온다.
 * 저장한 것만 그리면 세 화면이 같은 사실을 말한다.
 *
 * 컨텍스트를 안 읽는 순수 표현 컴포넌트다(어댑터는 ResultView) — 회차 하나만 있으면
 * 렌더가 성립해서 테스트가 화면 전체를 값으로 검증할 수 있다.
 */

/** 회차마다 다른 답(측정환경·컨디션)이 결과 해석의 근거다 — 값 위에 먼저 붙인다. */
const answerOf = (record: DiagnosisRecord, id: string): string | undefined => {
  const value = record.survey[id];
  return typeof value === 'string' ? value : undefined;
};

/**
 * 소음 경고가 붙는 측정환경. `outdoor`가 같이 있는 이유: 설문의 3지가 서버 LUT를 따라
 * `quiet`·`noisy`·`outdoor`로 바뀌기 전에는 `noisy` 한 값이 "야외·시끄러움"을 겸했다.
 * 야외를 빼면 그때 경고를 받던 사람이 조용히 못 받게 된다.
 */
const NOISY_ENVIRONMENTS = ['noisy', 'outdoor'] as const;

/**
 * 이 화면이 **탭으로** 세우는 축. 음역은 바로 위에서 자기 섹션(`RangeSection`)으로
 * 그리고 피로도는 이력을 비교하는 축이라 진단 직후에는 할 말이 없다 — 둘을 탭으로 또
 * 세우면 한 화면에 같은 그림이 두 번 선다. 일곱을 다 세우는 자리는 `/result/:id/:tab`
 * 상세다. 여기서 뺀 뒤에도 **값이 있는 탭만** 선다(`visibleTabs`).
 */
const OWN_TABS = [
  'registers',
  'vibrato',
  'breath',
  'formant',
  'resonance',
] as const;

/** 잰 방 바닥이 있으면 그것으로, 없을 때만(옛 회차·못 잡음) 설문 응답으로 판정한다. */
const isNoisy = (record: DiagnosisRecord): boolean =>
  record.roomFloorDbfs !== undefined
    ? isMeasuredNoisy(record.roomFloorDbfs)
    : NOISY_ENVIRONMENTS.some((v) => v === answerOf(record, 'environment'));

const NoiseWarning = () => {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-dashed border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
      {t('web.result.env.noisy')}
    </div>
  );
};

/**
 * 브라우저 개발/데모 경로는 합성 멜로디를 분석한 것이다 — 화면이 그렇게 말하지 않으면
 * 스크린샷 한 장이 실측 결과로 돌아다닌다. 저장도 안 된다(diagnosisStore).
 */
const MockNotice = () => {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-dashed border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
      {t('web.result.mock')}
    </div>
  );
};

export const ResultReport = ({
  record,
  previous = null,
  onTabView,
}: {
  record: DiagnosisRecord;
  /** 이력 대비의 기준선(직전 회차). 없으면 이력 칸이 `—`다. */
  previous?: DiagnosisRecord | null;
  /** 지표 탭을 누를 때마다 — 진단 직후 화면이 조회 수를 잰다(`useResultEngagement`). */
  onTabView?: (id: MetricTabId) => void;
}) => {
  const { t } = useTranslation();
  const condition = answerOf(record, 'condition');
  const visible = visibleTabs(record);
  const tabs = visible.filter((id) => OWN_TABS.some((own) => own === id));

  return (
    <div data-testid="result-report" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-xl font-bold">{t('web.result.title')}</h2>
        <p className="text-sm text-muted-foreground">
          {t('web.result.subtitle')}
        </p>
      </div>

      {/* 출처도 레코드에서 읽는다 — 컨텍스트의 reportSource와 같은 값이지만(둘 다
          `recordSource()`에서 나온다) 화면이 참조하는 사실은 한 군데여야 한다. */}
      {record.source === 'mock' && <MockNotice />}
      {isNoisy(record) && <NoiseWarning />}
      {condition && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t(`web.result.condition.${condition}`)}
        </p>
      )}

      <SustainSection record={record} />
      {visible.includes('range') && <RangeSection record={record} />}
      {tabs.length > 0 && (
        <ResultTabs
          record={record}
          previous={previous}
          only={tabs}
          onTabChange={onTabView}
        />
      )}
      <SongSection record={record} />
      <SummarySection record={record} />
    </div>
  );
};
